/**
 * Sezione Azienda - Logo, info aziendali e dati fiscali (Seller e Buyer)
 * Unifica le precedenti sezioni "Azienda" e "Dati Fiscali"
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { LogoCropEditor } from '../ui/logo-crop-editor';
import { VatLookupButton, VatLookupData } from '../ui/vat-lookup-button';
import AddressAutocomplete from '../AddressAutocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Alert,
  AlertDescription,
} from '../ui/alert';
import { 
  Building2, 
  Image, 
  Trash2, 
  Save,
  Globe,
  Shield,
  Loader2,
  Type,
  Crop,
  FileText,
  Mail,
  MapPin,
  Info,
  Cloud,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Province italiane
const PROVINCE_ITALIANE = [
  "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO", 
  "BR", "BS", "BT", "BZ", "CA", "CB", "CE", "CH", "CL", "CN", "CO", "CR", "CS", "CT", "CZ",
  "EN", "FC", "FE", "FG", "FI", "FM", "FR", "GE", "GO", "GR", "IM", "IS", "KR", "LC", "LE",
  "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT", "NA", "NO", "NU",
  "OR", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ", "RA",
  "RC", "RE", "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SU", "SV",
  "TA", "TE", "TN", "TO", "TP", "TR", "TS", "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VT", "VV"
];

const CompanySection = ({ user, company, fetchCompany, t }) => {
  const [activeTab, setActiveTab] = useState('generale');
  const logoInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState(company?.logo_url || '');
  const [logoCropOpen, setLogoCropOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    display_name: '',
    vat_number: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [requestingAccreditation, setRequestingAccreditation] = useState(false);
  
  // Stati per dati fiscali
  const [fiscalData, setFiscalData] = useState<any>({});
  const [fiscalLoading, setFiscalLoading] = useState(true);
  const [fiscalSaving, setFiscalSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [useSeparateBilling, setUseSeparateBilling] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    is_complete: boolean;
    missing_fields: string[];
    warnings: string[];
  } | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (company) {
      setLogoUrl(company.logo_url || '');
      setCompanyForm({
        name: company.name || '',
        display_name: company.display_name || '',
        vat_number: company.vat_number || '',
        address: company.address || '',
        phone: company.phone || '',
        email: company.email || '',
        website: company.website || '',
        description: company.description || ''
      });
    }
  }, [company]);

  useEffect(() => {
    fetchFiscalData();
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const fetchFiscalData = async () => {
    try {
      const [dataRes, checkRes] = await Promise.all([
        axios.get(`${API}/companies/my/fiscal`),
        axios.get(`${API}/companies/my/fiscal/check`)
      ]);
      
      const data = dataRes.data.fiscal_data || {};
      setFiscalData(data);
      setValidationResult(checkRes.data);
      
      if (data.billing_address || data.billing_city) {
        setUseSeparateBilling(true);
      }
      
      setTimeout(() => {
        isFirstLoad.current = false;
      }, 500);
    } catch (error) {
      console.error('Error fetching fiscal data:', error);
    } finally {
      setFiscalLoading(false);
    }
  };

  const handleLogoUpload = async (croppedBlob) => {
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', croppedBlob, 'logo.png');
      
      const res = await axios.post(`${API}/companies/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newLogoUrl = res.data.logo_url;
      setLogoUrl(newLogoUrl);
      toast.success('Logo caricato con successo');
      
      // Emit custom event to update logo in sidebar without page refresh
      window.dispatchEvent(new CustomEvent('company-logo-updated', { 
        detail: { logo_url: newLogoUrl } 
      }));
      
      fetchCompany();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nel caricamento del logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.delete(`${API}/companies/logo`);
      setLogoUrl('');
      toast.success('Logo rimosso');
      
      // Emit custom event to update sidebar
      window.dispatchEvent(new CustomEvent('company-logo-updated', { 
        detail: { logo_url: null } 
      }));
      
      fetchCompany();
    } catch (error) {
      toast.error('Errore nella rimozione del logo');
    }
  };

  const handleSubmitCompany = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/companies/my`, {
        ...companyForm,
        type: company?.type || user?.role || 'buyer',
        categories: company?.categories || [],
        tags: company?.tags || []
      });
      toast.success('Dati azienda aggiornati');
      fetchCompany();
    } catch (error: any) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const handleRequestAccreditation = async () => {
    setRequestingAccreditation(true);
    try {
      await axios.post(`${API}/directory/request-accreditation`);
      toast.success('Richiesta di accreditamento inviata! Un amministratore la esaminerà a breve.');
      fetchCompany();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nella richiesta di accreditamento'));
    } finally {
      setRequestingAccreditation(false);
    }
  };

  // Funzioni per dati fiscali
  const handleVatLookupData = (data: VatLookupData) => {
    const taxCode = data.tax_code || data.vat_code || '';
    const vatNumber = data.vat_code ? `IT${data.vat_code}` : '';
    
    setFiscalData(prev => ({
      ...prev,
      name: data.company_name || prev.name,
      vat_number: vatNumber || prev.vat_number,
      tax_code: taxCode || prev.tax_code,
      pec: data.pec || prev.pec,
      sdi_code: data.sdi_code || prev.sdi_code,
      address: data.street || prev.address,
      city: data.city || prev.city,
      postal_code: data.postal_code || prev.postal_code,
      province: data.province || prev.province,
    }));
  };

  const performFiscalAutoSave = useCallback(async (showToast: boolean = false) => {
    setFiscalSaving(true);
    try {
      const updateData: any = {
        vat_number: fiscalData.vat_number || null,
        tax_code: fiscalData.tax_code || null,
        sdi_code: fiscalData.sdi_code || null,
        pec: fiscalData.pec || null,
      };
      
      if (useSeparateBilling) {
        updateData.billing_address = fiscalData.billing_address || null;
        updateData.billing_city = fiscalData.billing_city || null;
        updateData.billing_postal_code = fiscalData.billing_postal_code || null;
        updateData.billing_province = fiscalData.billing_province || null;
        updateData.billing_country = fiscalData.billing_country || 'IT';
      } else {
        updateData.billing_address = null;
        updateData.billing_city = null;
        updateData.billing_postal_code = null;
        updateData.billing_province = null;
        updateData.billing_country = null;
      }
      
      await axios.put(`${API}/companies/my/fiscal`, updateData);
      
      await axios.patch(`${API}/companies/my`, {
        name: fiscalData.name,
        address: fiscalData.address,
        city: fiscalData.city,
        postal_code: fiscalData.postal_code,
        province: fiscalData.province,
        country: fiscalData.country || 'IT'
      });
      
      const checkRes = await axios.get(`${API}/companies/my/fiscal/check`);
      setValidationResult(checkRes.data);
      setLastSaved(new Date());
      
      if (showToast) {
        toast.success('Dati fiscali salvati');
      }
    } catch (error: any) {
      console.error('Error saving fiscal data:', error);
      if (showToast) {
        toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
      }
    } finally {
      setFiscalSaving(false);
    }
  }, [fiscalData, useSeparateBilling]);

  const triggerFiscalAutoSave = useCallback(() => {
    if (isFirstLoad.current) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      performFiscalAutoSave(false);
    }, 1500);
  }, [performFiscalAutoSave]);

  const handleFiscalChange = (field: string, value: string) => {
    setFiscalData(prev => ({ ...prev, [field]: value }));
    triggerFiscalAutoSave();
  };

  const handleFiscalBlur = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    performFiscalAutoSave(false);
  };

  const validateVatNumber = (vat: string): boolean => {
    if (!vat) return true;
    const cleaned = vat.replace(/\s/g, '').toUpperCase();
    return /^(IT)?\d{11}$/.test(cleaned);
  };

  const validateTaxCode = (cf: string): boolean => {
    if (!cf) return true;
    const cleaned = cf.replace(/\s/g, '').toUpperCase();
    return /^[A-Z0-9]{16}$/.test(cleaned) || /^\d{11}$/.test(cleaned);
  };

  const validateSdiCode = (sdi: string): boolean => {
    if (!sdi) return true;
    return /^[A-Z0-9]{7}$/i.test(sdi);
  };

  const getMissingFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      'name': 'Ragione Sociale',
      'vat_number_or_tax_code': 'Partita IVA o Codice Fiscale',
      'address': 'Indirizzo completo (via, città, CAP)'
    };
    return labels[field] || field;
  };

  // Mostra per seller e buyer (non per admin)
  if (user?.role === 'admin') return null;

  const isSeller = user?.role === 'seller';

  return (
    <div className="space-y-6" data-testid="company-section">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generale" className="gap-2">
            <Building2 className="h-4 w-4" />
            Generale
          </TabsTrigger>
          <TabsTrigger value="fiscale" className="gap-2">
            <FileText className="h-4 w-4" />
            Dati Fiscali
          </TabsTrigger>
        </TabsList>

        {/* TAB GENERALE */}
        <TabsContent value="generale" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Image className="h-5 w-5" />
                Logo e Identità
              </CardTitle>
              <CardDescription>
                Logo e nome visualizzati nella piattaforma
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload Section */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  Logo Aziendale
                </Label>
                <div className="flex items-center gap-4">
                  <div className="w-36 h-12 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center overflow-hidden bg-muted/30">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Logo aziendale" 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Image className="h-6 w-6 text-muted-foreground/50" />
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setLogoCropOpen(true)}
                      disabled={uploadingLogo}
                      data-testid="open-logo-editor-btn"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Caricamento...
                        </>
                      ) : (
                        <>
                          <Crop className="mr-2 h-4 w-4" />
                          {logoUrl ? 'Modifica Logo' : 'Carica Logo'}
                        </>
                      )}
                    </Button>
                    {logoUrl && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={handleRemoveLogo}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Rimuovi
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Formato rettangolare (3:1). Apparirà nella sidebar al posto del nome azienda.
                </p>
              </div>

              {/* Logo Crop Editor Dialog */}
              <LogoCropEditor
                open={logoCropOpen}
                onClose={() => setLogoCropOpen(false)}
                onSave={handleLogoUpload}
                cropShape="rect"
                aspectRatio={3}
                outputWidth={300}
                outputHeight={100}
                title="Modifica Logo Aziendale"
              />

              {/* Nome Breve per Sidebar */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <Label className="flex items-center gap-2">
                  <Type className="h-4 w-4" />
                  Nome Breve (per la barra laterale)
                </Label>
                <Input 
                  value={companyForm.display_name}
                  onChange={(e) => setCompanyForm({ ...companyForm, display_name: e.target.value })}
                  placeholder={companyForm.name || "Es. MiaAzienda"}
                  data-testid="company-display-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  {logoUrl 
                    ? "Quando è presente il logo, questo nome non verrà mostrato nella sidebar."
                    : "Questo nome apparirà nella barra laterale in alto a sinistra. Usa un nome breve se la ragione sociale è troppo lunga."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Company Info Form */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Contatti Azienda
              </CardTitle>
              <CardDescription>
                Recapiti per comunicazioni commerciali
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('company.phone')}</Label>
                  <Input 
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    placeholder="Es. +39 02 1234567"
                    data-testid="company-phone-input"
                  />
                </div>
                <div>
                  <Label>{t('company.email')}</Label>
                  <Input 
                    type="email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                    placeholder="info@azienda.it"
                    data-testid="company-email-input"
                  />
                </div>
                {isSeller && (
                  <>
                    <div className="md:col-span-2">
                      <Label className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Sito Web
                      </Label>
                      <Input 
                        value={companyForm.website}
                        onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                        placeholder="https://..."
                        data-testid="company-website-input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Descrizione Azienda</Label>
                      <Textarea 
                        value={companyForm.description}
                        onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
                        placeholder="Breve descrizione della tua attività..."
                        rows={3}
                        data-testid="company-description-input"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <Button onClick={handleSubmitCompany} disabled={saving} data-testid="save-company-btn">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {t('common.save')}
                    </>
                  )}
                </Button>

                {/* Directory Status - solo per seller */}
                {isSeller && (
                  <div className="flex items-center gap-2">
                    {company?.is_accredited ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        <Shield className="h-3 w-3 mr-1" />
                        Nella Directory Fornitori
                      </Badge>
                    ) : company?.accreditation_requested ? (
                      <Badge variant="secondary">
                        In attesa di approvazione
                      </Badge>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRequestAccreditation}
                        disabled={requestingAccreditation}
                        data-testid="request-accreditation-btn"
                      >
                        {requestingAccreditation ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Shield className="h-4 w-4 mr-2" />
                            Richiedi accreditamento
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB DATI FISCALI */}
        <TabsContent value="fiscale" className="space-y-6 mt-6">
          {fiscalLoading ? (
            <Card>
              <CardContent className="py-12 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Auto-save Status */}
              <div className="flex items-center justify-end gap-2 text-sm">
                {fiscalSaving ? (
                  <span className="inline-flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Salvataggio automatico...
                  </span>
                ) : lastSaved ? (
                  <span className="inline-flex items-center gap-2 text-muted-foreground">
                    <Cloud className="h-3.5 w-3.5 text-green-500" />
                    Salvato automaticamente
                  </span>
                ) : null}
              </div>

              {/* Dati Fiscali - PRIMO per VAT Lookup */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Dati Fiscali
                  </CardTitle>
                  <CardDescription className="flex items-center justify-between">
                    <span>Inserisci la Partita IVA per compilare automaticamente i dati</span>
                    <VatLookupButton 
                      onDataFound={handleVatLookupData}
                      vatValue={fiscalData.vat_number?.replace(/^IT/i, '') || ''}
                      size="sm"
                    />
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="vat_number">Partita IVA</Label>
                      <Input
                        id="vat_number"
                        value={fiscalData.vat_number || ''}
                        onChange={(e) => handleFiscalChange('vat_number', e.target.value.toUpperCase())}
                        onBlur={handleFiscalBlur}
                        placeholder="IT12345678901"
                        maxLength={13}
                        className={fiscalData.vat_number && !validateVatNumber(fiscalData.vat_number) ? 'border-red-500' : ''}
                      />
                      {fiscalData.vat_number && !validateVatNumber(fiscalData.vat_number) && (
                        <p className="text-xs text-red-500 mt-1">Formato non valido (11 cifre, opzionale prefisso IT)</p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="tax_code">Codice Fiscale</Label>
                      <Input
                        id="tax_code"
                        value={fiscalData.tax_code || ''}
                        onChange={(e) => handleFiscalChange('tax_code', e.target.value.toUpperCase())}
                        onBlur={handleFiscalBlur}
                        placeholder="RSSMRA80A01H501Z o 12345678901"
                        maxLength={16}
                        className={fiscalData.tax_code && !validateTaxCode(fiscalData.tax_code) ? 'border-red-500' : ''}
                      />
                      {fiscalData.tax_code && !validateTaxCode(fiscalData.tax_code) && (
                        <p className="text-xs text-red-500 mt-1">Formato non valido</p>
                      )}
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h4 className="font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Fatturazione Elettronica
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Per ricevere fatture elettroniche, inserisci il Codice SDI oppure la PEC
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sdi_code">Codice SDI</Label>
                      <Input
                        id="sdi_code"
                        value={fiscalData.sdi_code || ''}
                        onChange={(e) => handleFiscalChange('sdi_code', e.target.value.toUpperCase())}
                        onBlur={handleFiscalBlur}
                        placeholder="0000000"
                        maxLength={7}
                        className={fiscalData.sdi_code && !validateSdiCode(fiscalData.sdi_code) ? 'border-red-500' : ''}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        7 caratteri. Usa 0000000 se non hai un codice SDI.
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="pec">PEC</Label>
                      <Input
                        id="pec"
                        type="email"
                        value={fiscalData.pec || ''}
                        onChange={(e) => handleFiscalChange('pec', e.target.value.toLowerCase())}
                        onBlur={handleFiscalBlur}
                        placeholder="azienda@pec.it"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Alternativa al codice SDI
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sede Legale */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Sede Legale
                  </CardTitle>
                  <CardDescription>
                    Ragione sociale e indirizzo della sede legale
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="fiscal_name">Ragione Sociale *</Label>
                      <Input
                        id="fiscal_name"
                        value={fiscalData.name || ''}
                        onChange={(e) => handleFiscalChange('name', e.target.value)}
                        placeholder="Es. Mario Rossi S.r.l."
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <AddressAutocomplete
                        label="Indirizzo Sede Legale *"
                        value={fiscalData.address || ''}
                        onChange={(value) => handleFiscalChange('address', value)}
                        onAddressSelect={(data) => {
                          handleFiscalChange('address', data.address);
                          if (data.city) handleFiscalChange('city', data.city);
                          if (data.postal_code) handleFiscalChange('postal_code', data.postal_code);
                          if (data.province) handleFiscalChange('province', data.province);
                        }}
                        placeholder="Es: Via Roma 15, Milano"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="fiscal_city">Città *</Label>
                      <Input
                        id="fiscal_city"
                        value={fiscalData.city || ''}
                        onChange={(e) => handleFiscalChange('city', e.target.value)}
                        placeholder="Es. Milano"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="fiscal_postal_code">CAP *</Label>
                        <Input
                          id="fiscal_postal_code"
                          value={fiscalData.postal_code || ''}
                          onChange={(e) => handleFiscalChange('postal_code', e.target.value)}
                          placeholder="20100"
                          maxLength={5}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="fiscal_province">Provincia</Label>
                        <Select 
                          value={fiscalData.province || ''} 
                          onValueChange={(value) => handleFiscalChange('province', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="--" />
                          </SelectTrigger>
                          <SelectContent>
                            {PROVINCE_ITALIANE.map(prov => (
                              <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Indirizzo Fatturazione */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Indirizzo di Fatturazione
                  </CardTitle>
                  <CardDescription>
                    Se diverso dalla sede legale
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="separate_billing"
                      checked={useSeparateBilling}
                      onChange={(e) => setUseSeparateBilling(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="separate_billing" className="cursor-pointer">
                      Usa un indirizzo di fatturazione diverso
                    </Label>
                  </div>
                  
                  {useSeparateBilling && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                      <div className="md:col-span-2">
                        <Label htmlFor="billing_address">Indirizzo Fatturazione</Label>
                        <Input
                          id="billing_address"
                          value={fiscalData.billing_address || ''}
                          onChange={(e) => handleFiscalChange('billing_address', e.target.value)}
                          placeholder="Es. Via Milano, 456"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="billing_city">Città</Label>
                        <Input
                          id="billing_city"
                          value={fiscalData.billing_city || ''}
                          onChange={(e) => handleFiscalChange('billing_city', e.target.value)}
                          placeholder="Es. Roma"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="billing_postal_code">CAP</Label>
                          <Input
                            id="billing_postal_code"
                            value={fiscalData.billing_postal_code || ''}
                            onChange={(e) => handleFiscalChange('billing_postal_code', e.target.value)}
                            placeholder="00100"
                            maxLength={5}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="billing_province">Provincia</Label>
                          <Select 
                            value={fiscalData.billing_province || ''} 
                            onValueChange={(value) => handleFiscalChange('billing_province', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="--" />
                            </SelectTrigger>
                            <SelectContent>
                              {PROVINCE_ITALIANE.map(prov => (
                                <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Status Card - in fondo come riepilogo */}
              <Card className={validationResult?.is_complete ? 'border-green-200 bg-green-50/50 dark:bg-green-900/10' : 'border-amber-200 bg-amber-50/50 dark:bg-amber-900/10'}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    {validationResult?.is_complete ? (
                      <CheckCircle className="h-6 w-6 text-green-600 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h3 className={`font-semibold ${validationResult?.is_complete ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
                        {validationResult?.is_complete ? 'Dati fiscali completi' : 'Dati fiscali incompleti'}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {validationResult?.is_complete 
                          ? 'I tuoi dati fiscali sono completi. Puoi emettere e ricevere fatture.'
                          : 'Completa i dati fiscali per poter emettere e ricevere fatture elettroniche.'
                        }
                      </p>
                      
                      {validationResult && !validationResult.is_complete && validationResult.missing_fields.length > 0 && (
                        <div className="mt-3 space-y-1">
                          <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Campi mancanti:</p>
                          <ul className="list-disc list-inside text-sm text-muted-foreground">
                            {validationResult.missing_fields.map(field => (
                              <li key={field}>{getMissingFieldLabel(field)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompanySection;
